import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type PilotEnrollment = {
  id: string;
  pilot_id: string;
  cohort: string;
  start_date: string;
  end_date: string;
  ended_early: boolean;
  pilot?: { name: string } | null;
};

/**
 * The signed-in user's pilot enrollment (if any) and whether they are a
 * "pilot-only assessor" — assessor role with a pilot enrollment and no
 * admin/approver/reviewer privileges. Pilot-only assessors collect data
 * but never see scores, reports or analytics.
 */
export function usePilotEnrollment() {
  const { user, roles } = useAuth();

  const { data: enrollment = null, isLoading } = useQuery({
    queryKey: ["pilot-enrollment", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("pilot_enrollments")
        .select("id, pilot_id, cohort, start_date, end_date, ended_early, pilot:pilot_programs(name)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return (data as unknown as PilotEnrollment) ?? null;
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  const privileged = roles.some((r) => r === "admin" || r === "approver");
  const isPilotOnlyAssessor = !!enrollment && roles.includes("assessor") && !privileged;
  const isExpired =
    !!enrollment && (enrollment.ended_early || new Date(enrollment.end_date).getTime() < Date.now());

  return { enrollment, isPilotOnlyAssessor, isExpired, loading: isLoading };
}
